import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild,
    inject
} from "@angular/core";
import { MapLayersComponent } from "@components/map/components/menu/map-layers/map-layers.component";
import { MapViapunkterComponent } from "@components/map/components/menu/map-viapunkter/map-viapunkter.component";
import { MapComponent } from "@components/map/map.component";
import { IMapComponent } from "~app/common/components/map/map.models";
import { IMapDrawLayer, IMapGeometryPoint, IMapSettings } from "~app/common/components/map/map.settings";
import { IMapQueryResult } from "~app/common/components/map/services/map-query.service";
import { TplMapLayerConstats } from "~app/common/models/constants";
import { EventService } from "~app/common/services/cqs/event.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ITplDestineringRuttQueryResult } from "../../../models/queries";
import {
    DeleteViapunktEvent,
    PointLocationChangedEvent,
    RuttChangedEvent,
    StartpunktResetEvent,
    TplTransportAvstandEventHandler,
    ViapunktAddedEvent,
    ViapunkterModifiedEvent
} from "../tpl-transportavstand-eventhandler";
import { TplTransportavstandKartaViapunktDialogComponent } from "./dialogs/viapunkt/viapunkt-dialog.component";
import { TplTransportavstandKartaRutt } from "./layers/tpl-transportavstand-karta.rutt";
import { TplTransportavstandKartaSlutpunkt } from "./layers/tpl-transportavstand-karta.slutpunkt";
import { TplTransportavstandKartaStartpunkt } from "./layers/tpl-transportavstand-karta.startpunkt";
import { TplTransportavstandKartaViapunkter } from "./layers/tpl-transportavstand-karta.viapunkter";

@Component({
    selector: "rask-tpl-transportavstand-karta",
    templateUrl: "./tpl-transportavstand-karta.component.html",
    styleUrls: ["./tpl-transportavstand-karta.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MapComponent,
        MapViapunkterComponent,
        MapLayersComponent
    ]
})
export class TplTransportavstandKartaComponent implements OnInit, OnDestroy {
    clickedItem = {
        visible: false,
        label: "",
        route: null,
        id: null
    };
    @Input() data: ITplDestineringRuttQueryResult;
    @Output() mapReady = new EventEmitter();
    featureLayers: IMapQueryResult[];
    map: IMapComponent;
    mapsettings: IMapSettings;
    @ViewChild("popup", { static: true }) popup: ElementRef;
    selectedGeometry: any;
    private readonly eventHandler: TplTransportAvstandEventHandler;
    private readonly eventService = inject(EventService);
    private readonly kartaRutt: TplTransportavstandKartaRutt;
    private readonly kartaSlutpunkt: TplTransportavstandKartaSlutpunkt;
    private readonly kartaStartpunkt: TplTransportavstandKartaStartpunkt;
    private readonly kartaViapunkter: TplTransportavstandKartaViapunkter;
    private readonly renderer = inject(Renderer2);

    constructor() {
        this.kartaRutt = new TplTransportavstandKartaRutt();
        this.kartaStartpunkt = new TplTransportavstandKartaStartpunkt(this);
        this.kartaSlutpunkt = new TplTransportavstandKartaSlutpunkt(this);
        this.kartaViapunkter = new TplTransportavstandKartaViapunkter(this);

        this.eventHandler = new TplTransportAvstandEventHandler(this.eventService);
    }

    deleteViapunkt(easting: number, northing: number) {
        const activeBkKlass = this.data.arbetsorderTransportavtand.find(x => x.active);
        const index = activeBkKlass.viaPunkter.findIndex(x => x.easting === easting && x.northing === northing);
        activeBkKlass.viaPunkter.splice(index, 1);

        const event = new DeleteViapunktEvent({
            easting: easting,
            northing: northing
        });
        this.eventService.emit(event);

        this.kartaViapunkter.load(this.data, this.mapsettings, this.map);
    }

    hideTooltip() {
        this.clickedItem.visible = false;
    }

    ngOnDestroy(): void {
        this.eventHandler.unsubscribe();
    }

    ngOnInit(): void {
        const drawlayers = this.createDrawLayers();
        this.mapsettings = {
            id: "justaraRutt",
            height: this.getHeight(),
            coords: true,
            zoomButtons: true,
            onMapReady: (map) => this.onMapReady(map),
            onLayerReady: (map, layer) => this.onLayerReady(map, layer),
            featurelayers: [
                { id: "Vagar" },
                { id: "VFS_PlaneradVag" },
                { id: "smittad_zon_sjv" }
            ],
            drawLayers: drawlayers,
            icons: [

                {
                    id: "startpunktIcon",
                    color: "#0c9427",
                    size: 14,
                    outlineWidth: 0,
                    arcgisStyle: "path",
                    yoffset: 6,
                    outlineColor: "#0c9427",
                    iconSvgPath: "M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0z"
                },

                {
                    id: "slutpunktIcon",
                    color: "#c70b18",
                    size: 14,
                    outlineWidth: 0,
                    arcgisStyle: "path",
                    yoffset: 6,
                    outlineColor: "#c70b18",
                    iconSvgPath: "M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z"
                },

                {
                    id: "viapunktIcon",
                    color: "#e27508",
                    size: 14,
                    outlineWidth: 0,
                    arcgisStyle: "path",
                    yoffset: 6,
                    outlineColor: "#e27508",
                    iconSvgPath: "M192 0C86.4 0 0 86.4 0 192c0 76.8 25.6 99.2 172.8 310.4 9.6 12.8 28.8 12.8 38.4 0C358.4 291.2 384 268.8 384 192 384 86.4 297.6 0 192 0zm112 200c0 8.84-7.16 16-16 16h-72v72c0 8.84-7.16 16-16 16h-16c-8.84 0-16-7.16-16-16v-72H96c-8.84 0-16-7.16-16-16v-16c0-8.84 7.16-16 16-16h72V96c0-8.84 7.16-16 16-16h16c8.84 0 16 7.16 16 16v72h72c8.84 0 16 7.16 16 16v16z"
                }
            ]
        };

        this.eventHandler.onViapunktAdded(e => {
            this.onViapunktAdded(e);
        });

        this.eventHandler.onViapunktDeleted(e => {
            this.onViapunktDeleted(e);
        });

        this.eventHandler.onRuttChanged(e => {
            this.onRuttChanged(e);
        });

        this.eventHandler.onStartpunktReset(e => {
            this.onStartpunktReset(e);
        });
    }

    onLayerReady(map: IMapComponent, layer: string) {
        switch (layer) {
            case "startpunkt":
                this.kartaStartpunkt.load(this.data, this.mapsettings, this.map);
                break;
            case "slutpunkt":
                this.kartaSlutpunkt.load(this.data, this.mapsettings, this.map);
                break;
            case TplMapLayerConstats.ruttLayerName:
                this.kartaRutt.load(this.data, this.mapsettings, this.map, layer);
                break;
            case "viapunkter":
                this.kartaViapunkter.load(this.data, this.mapsettings, this.map);
                break;
        }
    }

    onMapReady(map: IMapComponent) {
        this.map = map;
        this.mapReady.emit();
    }

    selectPoint(mouseevent: any, geometry: any, layerId: string) {
        const layer = this.mapsettings.drawLayers.find(x => x.id === layerId);
        const geometryBeforeChange = ObjectUtils.deepCopy(geometry);
        this.map.getSketch().edit(layer, geometry, (g) => {
            geometry = g.wkt;
            this.geometryLocactionChanged(g, geometryBeforeChange);
        });

        if (layerId === "viapunkter" && ObjectUtils.hasValue(geometry)) {
            setTimeout(() => {
                this.showTooltip(geometry, mouseevent.clientX, mouseevent.clientY);
            }, 20);
        } else {
            this.hideTooltip();
        }
        return false;
    }

    showTooltip(geometry: any, x: number, y: number, id?: number) {
        const element = this.popup.nativeElement;
        this.renderer.setStyle(element, "top", `${y - 92}px`);
        this.renderer.setStyle(element, "left", `${x - 80}px`);
        this.renderer.setStyle(element, "display", "block");
        if (geometry?.id === "slutpunkt") {
            this.clickedItem.label = this.data.mottagningsplats;
        } else if (geometry?.id.indexOf("viapunkter") > -1) {
            this.clickedItem.label = "Via-punkt";
        } else {
            this.clickedItem.label = geometry?.label;
        }
        this.clickedItem.id = id;
        this.clickedItem.visible = true;
        this.selectedGeometry = ObjectUtils.hasValue(geometry) ? geometry : null;
    }

    private createDrawLayers(): IMapDrawLayer[] {
        const rutt = this.kartaRutt.getLayer(TplMapLayerConstats.ruttLayerName, "Rutt");
        const startpunkt = this.kartaStartpunkt.getLayer();
        const slutpunkt = this.kartaSlutpunkt.getLayer();
        const viapunkter = this.kartaViapunkter.getLayer();

        return [
            rutt,
            startpunkt,
            slutpunkt,
            viapunkter
        ] as IMapDrawLayer[];
    }

    private geometryLocactionChanged(geometry: IMapGeometryPoint, geometryBeforeChange: IMapGeometryPoint) {
        const event = new PointLocationChangedEvent(geometry, geometryBeforeChange);
        this.eventService.emit(event);
    }

    private getHeight(): string {
        const wh = window.innerHeight;
        const height = wh - 300;
        return `${height}px`;
    }

    private onRuttChanged(e: RuttChangedEvent) {
        if (this.map) {
            this.kartaRutt.load(this.data, this.mapsettings, this.map, TplMapLayerConstats.ruttLayerName);
            this.kartaViapunkter.load(this.data, this.mapsettings, this.map);
        }
    }

    private onStartpunktReset(e: StartpunktResetEvent) {
        this.data.startPunkt.easting = e.easting;
        this.data.startPunkt.northing = e.northing;
        this.kartaStartpunkt.load(this.data, this.mapsettings, this.map);
    }

    private onViapunktAdded(e: ViapunktAddedEvent) {
        this.data.arbetsorderTransportavtand.forEach(x => {
            const index = x.viaPunkter.length === 0
                ? 0
                // eslint-disable-next-line prefer-spread
                : Math.max.apply(Math, x.viaPunkter.map((o) => o.index));
            x.viaPunkter.push({
                easting: e.point.x,
                northing: e.point.y,
                index: index + 1
            });
        });
        this.kartaViapunkter.load(this.data, this.mapsettings, this.map);
        const geometries = this.mapsettings.drawLayers.find(x => x.id === "viapunkter").geometries;
        const obs = this.map.showPopupOnGeometry(
            TplTransportavstandKartaViapunktDialogComponent,
            geometries[geometries.length - 1],
            {
                data: this.data,
                gemometri: e.point
            }
        );
        obs?.subscribe(x => {
            const event = new ViapunkterModifiedEvent(x.instance.uppdateraAllaMottagningsplatser);
            this.eventService.emit(event);
        });
    }

    private onViapunktDeleted(e: DeleteViapunktEvent) {
        const activeBkKlass = this.data.arbetsorderTransportavtand.find(x => x.active);
        const index = activeBkKlass.viaPunkter.findIndex(x => x.easting === e.point.easting && x.northing === e.point.northing);
        if (index > -1) {
            activeBkKlass.viaPunkter.splice(index, 1);
        }
        this.kartaViapunkter.load(this.data, this.mapsettings, this.map);
    }
}
